
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks'

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { RootState } from '@/store';
import { updateBankDetails, setBankDetailsCompleted, setLoading } from '@/store/slices/profileSlice';
import {  AlertCircle,AlertTriangle, InfoIcon, Landmark } from 'lucide-react';

import { Address, BankDetails } from '@/types/interfaceType'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog"
import { ButtonWithLoader } from '../ui/buttonwithloader';
import { createVendorFundAccount ,validateVendorBankAccount} from '@/store/thunkMidddleware/usersMiddleware';
import Lottie from "lottie-react";
import CommonPopup from "@/components/popups/commonPopup";
import animation from '@/assets/animation/animation';
import axios from 'axios';

interface BankDetailsFormProps {
  onNext?: () => void;
  onSkip?: () => void;
  goBack?: () =>  void;
  updateProfileBank: (bankDetails?: BankDetails, callback?: (res: any) => void) => Promise<void>;

}


type ConfirmationDialogProps = {
  showConfirmModal: boolean;
  setShowConfirmModal: (val: boolean) => void;
  onConfirm: () => void; // callback when vendor confirms
};

export const ConfirmationDialog = ({
  showConfirmModal,
  setShowConfirmModal,
  onConfirm,
}: ConfirmationDialogProps) => {
  const { bankDetails ,updateVendorProfile,createVendorFundAccountResponse} = useAppSelector((state: RootState) => state.profile);

  return (
    <Dialog
      open={showConfirmModal}
      onOpenChange={(isOpen) => setShowConfirmModal(isOpen)}
    >
      <DialogContent className="max-w-[500px] max-h-[90vh] p-0 bg-flup-surface flex flex-col">
        {/* Header */}
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="text-lg font-semibold text-black/80 flex justify-start items-center">
           <Landmark className="w-5 h-5 text-black/80 font-bold mr-2"/>Confirm Bank Account
          </DialogTitle>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6  space-y-4">
          <p className="text-sm text-flup-ink font-medium">
            You are about to create a <span className="font-bold">payout account </span> 
            for fund transfers. Please review your bank details carefully before confirming:
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className='font-bold'>Account Holder Name</Label>
              <p className="text-sm">{bankDetails?.accountHolderName || "N/A"}</p>
            </div>

            <div>
              <Label className='font-bold'>Bank Name</Label>
              <p className="text-sm">{bankDetails?.bankName || "N/A"}</p>
            </div>

            <div>
              <Label className='font-bold'>Account Number</Label>
              <p className="text-sm">{bankDetails?.accountNumber || "N/A"}</p>
            </div>

            <div>
              <Label className='font-bold'>IFSC Code</Label>
              <p className="text-sm">{bankDetails?.ifsc || "N/A"}</p>
            </div>
          </div>

          <p className="text-xs  mt-2 flex items-center space-x-2">
            <InfoIcon className='w-6 h-6 text-muted-foreground'/>
            <span className="font-medium text-muted-foreground">
              Once confirmed, these details will be used for fund transfers. Make sure the details are correct.
            </span>
          </p>
        </div>

        {/* Footer */}
        <DialogFooter className="p-4 border-t flex gap-4">
          <Button
            variant="outline"
            onClick={() => setShowConfirmModal(false)}
            className="flex-1"
            disabled={updateVendorProfile.isLoading}
          >
            Cancel
          </Button>
          <ButtonWithLoader
          isLoading={updateVendorProfile.isLoading ||  createVendorFundAccountResponse.isLoading}
          disabled={updateVendorProfile.isLoading || createVendorFundAccountResponse.isLoading}
            onClick={() => {
              onConfirm();
            }}
            className="flex-1 bg-green-600 text-white hover:bg-green-700"
          >
            Confirm & Save
          </ButtonWithLoader>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};



const BankDetailsForm: React.FC<BankDetailsFormProps> = ({ onNext, onSkip, updateProfileBank ,goBack}) => {
  const dispatch = useAppDispatch();
  const { bankDetails, loading ,updateVendorProfile} = useAppSelector((state: RootState) => state.profile);
  const { BanksList} = useAppSelector((state: RootState) => state.turf);
  const [showConfirmModal,setShowConfirmModal]= useState(false)
  const { toast } = useToast();
  const [localData, setLocalData] = useState(bankDetails);
  const [initialData] = useState(bankDetails);
  const [showBankVerifiction,setShowBankVerifiction] = useState(false)
  const [bank,setBankDetails]= useState(null)
  const [bankError,setBankError]= useState(null)

  const [isValidIFSC,setIsValidIFSC]= useState(null)
  const  [isLoading,setIsLoading] = useState(false)


  const fetchBankName=async(ifsc:string)=>{
    try {
      let res= await axios.get(`https://ifsc.razorpay.com/${ifsc}`)
      setBankDetails(res?.data)
      if(res?.data?.BANK){
            setBankError(null)
            handleInputChange('bankName', res?.data?.BANK)   
         }
    } catch (error) {
      setBankDetails(null)
      if(error?.response){
        setBankError(error?.response?.statusText || "Not Found")
      }
      console.log("fetchBankName error",error)
    }
  }

  useEffect(()=>{
    if(localData?.ifsc && validateIFSC(localData?.ifsc)){
      fetchBankName(localData?.ifsc)
    }
  },[localData?.ifsc])
  const isBankAccountChanged = () => {
    return JSON.stringify(initialData) !== JSON.stringify(localData);
  };



  const handleInputChange = (field: string, value: string) => {
    const updatedData = { ...localData, [field]: value };
    setLocalData(updatedData);
    dispatch(updateBankDetails({ [field]: value }));
  };

  const validateIFSC = (ifsc: string): boolean => {
    return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc);
  };

  const validateUPI = (upi: string): boolean => {
    return /^[\w\.\-_]{3,}@[\w\-_]{2,}$/.test(upi);
  };

  const validateForm = (): boolean => {
    if ((localData.bankName?.length<3) || !localData.bankName) {
      toast({ title: 'Error', description: 'Please enter valid Bank Name', variant: 'destructive' });
      return false;
    }

    if ((localData.accountHolderName?.length<3) || !localData.accountHolderName) {
      toast({ title: 'Error', description: 'Please enter valid account holder name', variant: 'destructive' });
      return false;
    }

    if ((localData.accountNumber?.length<6 ) || !localData.accountNumber) {
      toast({ title: 'Error', description: 'Please enter account number', variant: 'destructive' });
      return false;
    }


    if ((localData.ifsc && !validateIFSC(localData.ifsc)) || !localData.ifsc || bankError) {
      toast({ title: 'Error', description: 'Please enter a valid IFSC code', variant: 'destructive' });
      return false;
    }

    if (localData.upiId && !validateUPI(localData.upiId)) {
      toast({ title: 'Error', description: 'Please enter a valid UPI ID', variant: 'destructive' });
      return false;
    }

    return true;
  };


  const validateBankAccount=()=>{
    setShowBankVerifiction(true)
      setTimeout(() => {
        dispatch(validateVendorBankAccount({callback:(res)=>{
          debugger
          if(res?.success){
            setShowBankVerifiction(false)
            proceedNextScreen()
          }
        }}))
      }, 2000);
  }

  const proceedNextScreen=()=>{
    dispatch(setBankDetailsCompleted(true));
    // toast({ title: 'Success', description: 'Bank details saved successfully!' });
    onNext();
  }
  const createFundAccount =async()=>{
    try {
      dispatch(createVendorFundAccount({callback:(res)=>{
        if(res?.success){
          setShowConfirmModal(false);
          validateBankAccount()
          // proceedNextScreen()
        }
      }}))
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save bank details', variant: 'destructive' });
    } finally {
        dispatch(setLoading(false));
      }
    }
  

  const updateProfileBankHandler =async()=>{
    try {
      // Simulate API call
      // await new Promise(resolve => setTimeout(resolve, 1000));
      let payload = {
        accountHolderName: localData.accountHolderName?.trim(),
        bankName: localData.bankName?.trim(),
        accountNumber: localData.accountNumber?.trim(),
        ifsc: localData.ifsc?.trim(),
        upiId: localData.upiId?.trim(),
      }
    // 🚀 If nothing changed → skip API
      if (!isBankAccountChanged()) {
        createFundAccount()
        return;
      }
      

      await updateProfileBank(payload, (profile: any) => {
        console.log("updateProfile response:", profile)
        if (profile?.success) {
          createFundAccount()
          // dispatch(setBankDetailsCompleted(true));
          // toast({ title: 'Success', description: 'Bank details saved successfully!' });
          // onNext();
        }
      })
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save bank details', variant: 'destructive' });
    } finally {
      dispatch(setLoading(false));
    }
  }

  const handleSubmit = async () => {
    if (!validateForm()) return;    
    dispatch(setLoading(true));
    setShowConfirmModal(true)
  };


  const handleSkip = () => {
    goBack()
    // toast({ title: 'Info', description: 'You can add bank details later from your profile' });
    // onSkip();
  };


  const renderBankVerificationModal=()=>{
    return <CommonPopup
    isOpen={showBankVerifiction}
    title={"Bank Verification"}
    headerIcon={<Landmark className="w-4 h-4 text-blue-800 font-bold"/>}
    showCloseIcon={false}
    showDefaultFooter={false}
    description={<>
     <Lottie
         animationData={animation.BankTransfer} loop={true}
         className='w-full'
          // style={{ width: 200, height: 200 }}
       />
       <p className='text-sm text-center'>Please wait while we transfer ₹1 to your bank account for verification</p>
    </>}
    />
  }
  const isChanged = isBankAccountChanged();


  return (
    // <div className="min-h-screen flex items-center justify-center">
    <>
    <ConfirmationDialog
        showConfirmModal={showConfirmModal}
        setShowConfirmModal={setShowConfirmModal}
        onConfirm={() => {
          updateProfileBankHandler();
          }}
      />
      {renderBankVerificationModal()}
    <div className="flex items-center justify-center w-96 sm:w-full p-4 sm:p-0">

      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Bank Details</CardTitle>
          <CardDescription>Add your bank details for payment processing (Optional)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* <div className="space-y-2">
            <Label htmlFor="bankName">Bank Name</Label>

<Select value={localData.bankName || ""}
                onValueChange={(value) => handleInputChange('bankName', value)}
                disabled={updateVendorProfile.isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Bank Name" />
                </SelectTrigger>
                <SelectContent>
                  {BanksList?.length>0 && BanksList?.map((ele:any,idx) => (
                    <SelectItem key={idx} value={ele?.name} >
                      {ele?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

          </div> */}

          <div className="space-y-2">
            <Label htmlFor="accountHolderName">Account Holder Name</Label>
            <Input
              id="accountHolderName"
              value={localData.accountHolderName}
              onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
              placeholder="Enter account holder name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="accountNumber">Account Number</Label>
            <Input
              id="accountNumber"
              value={localData.accountNumber}
              onChange={(e) => handleInputChange('accountNumber', e.target.value)}
              placeholder="Enter account number"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ifsc">IFSC Code</Label>
            <Input
              id="ifsc"
              value={localData.ifsc}
              onChange={(e) => handleInputChange('ifsc', e.target.value.toUpperCase())}
              placeholder="Enter IFSC code (e.g., SBIN0001234)"
            />
            {localData.ifsc && !validateIFSC(localData.ifsc) && (
              <p className="text-xs text-red-500">Invalid IFSC code format</p>
            )}

            {bankError && validateIFSC(localData.ifsc) && 
              <p className="text-xs text-red-500 ml-2 ">Bank Details {bankError}</p>
            }

            {validateIFSC(localData.ifsc) && bank &&
            <div className='text-xs text-muted-foreground ml-2'>

            <p className=''>{bank?.BANK} </p>
            <p>{bank?.BRANCH} </p>
            <p>{bank?.CITY} </p>

            </div>

            }
          </div>

          <div className="space-y-2">
            <Label htmlFor="upiId">UPI ID</Label>
            <Input
              id="upiId"
              value={localData.upiId}
              onChange={(e) => handleInputChange('upiId', e.target.value)}
              placeholder="Enter UPI ID (e.g., user@paytm)"
            />
            {localData.upiId && !validateUPI(localData.upiId) && (
              <p className="text-sm text-red-500">Invalid UPI ID format</p>
            )}
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              variant="outline"
              onClick={handleSkip}
              className="flex-1"
            >
              Go Back
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 bg-flup-brand hover:bg-flup-brand-press text-white"
              disabled={updateVendorProfile.isLoading}
            >
                {isChanged ? 
              updateVendorProfile.isLoading ? 'Saving...' : 'Save & Continue'
              : 'Continue'}

            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default BankDetailsForm;
